import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    DisplayNotice,
    FlyFreelyError,
    FlyFreelyLoggingService,
    Lotam,
    NoticeDto,
    NoticeType,
    NoticesService,
    PersonsOrganisationDto,
    WorkTracker,
    hasAnyPermission,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableAction,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, combineLatest } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FormatAltitudeValuePipe } from '../format-altitude-value.pipe';
import {
    LotamDataService,
    formatLotamAudience,
    formatLotamPriority,
    formatOperatingRequirement
} from '../lotam-data.service';
import { LotamDialoguesService } from '../lotam-dialogues.service';

@Component({
    selector: 'lotam-list',
    templateUrl: './lotam-list.component.html',
    styleUrls: ['./lotam-list.component.scss']
})
export class LotamListComponent implements OnInit, OnDestroy {
    @Input() organisation?: PersonsOrganisationDto;
    availableColumns: TableColumn[];
    selectedColumns?: string[];
    noticeTypes: NoticeType[] = [];

    lotams?: DisplayNotice[];
    tableConfig: TableConfig;
    columnSorting?: ColumnSortPreferences;
    tableSearch: any;
    textSearch: string | null = '';
    private searchSubject = new Subject<string | null>();

    canAddNotice = false;

    public working = false;
    private workTracker = new WorkTracker();

    private ngUnsubscribe$ = new Subject<void>();

    protected readonly NoticeDto = NoticeDto;
    protected readonly formatAudience = formatLotamAudience;
    protected readonly formatLotamPriority = formatLotamPriority;
    protected readonly formatOperatingRequirement = formatOperatingRequirement;

    constructor(
        private workspaceStateService: WorkspaceStateService,
        private lotamDataService: LotamDataService,
        private lotamDialoguesService: LotamDialoguesService,
        private modal: BsModalRef<LotamListComponent>,
        private noticesService: NoticesService,
        private logging: FlyFreelyLoggingService,
        formatAltitudeValuePipe: FormatAltitudeValuePipe
    ) {
        this.availableColumns = [
            {
                value: 'subject',
                name: 'LOTAM',
                searchable: true,
                selectable: false,
                defaultSelection: true,
                linkFunction: (lotam: DisplayNotice) => this.viewElement(lotam)
            },
            {
                value: 'description',
                name: 'Description',
                searchable: true,
                selectable: false,
                defaultSelection: true,
                sortable: false
            },
            {
                value: 'noticeType.name',
                name: 'Type',
                searchable: 'selection',
                selectable: false,
                defaultSelection: true,
                sortable: true,
                searchOptions: [],
                clearableSearch: true
            },
            {
                value: 'activityTime',
                name: 'Activity Time',
                searchable: 'daterange',
                selectable: false,
                defaultSelection: true,
                sortable: false
            },
            {
                value: 'pointOfContact',
                name: 'Contact Name',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                sortable: false
            },
            {
                value: 'pointOfContactPhoneNumber',
                name: 'Contact Phone',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                sortable: false
            },
            {
                value: 'importance',
                name: 'Priority',
                searchable: true,
                selectable: false,
                defaultSelection: true,
                searchOptions: [
                    { name: 'Low', value: 'LOW' },
                    { name: 'Medium', value: 'MEDIUM' },
                    { name: 'High', value: 'HIGH' }
                ]
            },
            {
                value: 'operatingRequirement',
                name: 'Operating Requirement',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                sortable: false,
                formatterFunction: (val: string) =>
                    formatOperatingRequirement[val] ?? val
            },
            {
                value: 'lowerLimit',
                name: 'Lower Limit',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                sortable: false,
                formatterFunction: val =>
                    formatAltitudeValuePipe.transform(val, 'SFC')
            },
            {
                value: 'upperLimit',
                name: 'Upper Limit',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                sortable: false,
                formatterFunction: val =>
                    formatAltitudeValuePipe.transform(val, 'Unlimited')
            }
        ];
        this.tableConfig = {
            serverPagination: true,
            limit: 10,
            actions: this.buildTableActions(),
            limitSelection: [10, 25, 50, 100]
        };
        combineLatest([
            this.lotamDataService.working$,
            this.workTracker.observable
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([serviceWorking, working]) => {
                this.working = working || serviceWorking;
            });
    }

    ngOnInit(): void {
        this.refreshSubscriptions();

        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.setupPermissions();
                this.resetState();
                this.loadNoticeTypes();
                this.refreshLotam();
            });

        this.searchSubject
            .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe$))
            .subscribe(term => {
                this.textSearch = term || null;
                this.refreshLotam();
            });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onSearch(term: string) {
        this.searchSubject.next(term);
    }

    clearSearch() {
        this.searchSubject.next('');
        (document.querySelector('.search-input') as HTMLInputElement).value =
            '';
    }

    onTableSearch(search: any) {
        this.tableSearch = search;
        if (this.tableConfig != null) {
            this.tableConfig.currentPage = 0;
        }
        this.refreshLotam();
    }

    viewElement(data: DisplayNotice) {
        this.lotamDialoguesService.showLotamDetailDialogue(
            data.id,
            this.organisation.id
        );
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        this.refreshLotam();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.refreshLotam();
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        this.refreshLotam();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.columnSorting =
            sorting == null
                ? null
                : {
                      column: sorting.column.toString(),
                      ascending: sorting.ascending
                  };
        this.tableConfig.currentPage = 0;
        this.refreshLotam();
    }

    private loadNoticeTypes() {
        this.noticesService
            .findNoticeTypes({
                organisationId: this.organisation.id,
                managingOrganisationId: this.organisation.id,
                archived: false,
                audience: 'FLIGHT_OPERATIONS'
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: types => {
                    this.noticeTypes = types;
                    this.updateNoticeTypeColumn();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error loading notice types: ${error.message}`
                    )
            });
    }

    private updateNoticeTypeColumn() {
        const options = this.noticeTypes.map(t => ({
            name: t.name,
            value: t.id
        }));
        this.availableColumns = this.availableColumns.map(col =>
            col.value === 'noticeType.name'
                ? { ...col, searchOptions: options }
                : col
        );
    }

    private setupPermissions() {
        this.canAddNotice = hasAnyPermission(this.organisation!, [
            PersonsOrganisationDto.Permissions.LOTAM_ADD
        ]);
    }

    /**
     * Resets the component back to its initial state.
     * Typically called after organisation change.
     * Clears the LOTAM list and resets table preferences to be reloaded.
     */
    resetState() {
        this.lotams = [];
        this.tableConfig = {
            serverPagination: true,
            limit: 10,
            limitSelection: [10, 25, 50, 100],
            currentPage: 0,
            totalItems: 0,
            actions: this.buildTableActions()
        };
        this.tableSearch = null;
        this.clearSearch();
    }

    refreshLotam() {
        const parsedTableSearch = this.tableSearch
            ? {
                  subject: this.tableSearch.subject,
                  description: this.tableSearch.description,
                  importance: this.tableSearch.importance,
                  acknowledged: this.tableSearch.acknowledged,
                  noticeTypeId:
                      this.tableSearch['noticeType.name'] ?? undefined,
                  ...(this.tableSearch.activityTime != null
                      ? {
                            date: {
                                start: toTimestamp(
                                    this.tableSearch.activityTime[0]
                                ),
                                end: toTimestamp(
                                    this.tableSearch.activityTime[1]
                                )
                            }
                        }
                      : {})
              }
            : {};
        this.lotamDataService.findLotams({
            page: this.tableConfig.currentPage ?? 0,
            limit: this.tableConfig.limit ?? 3,
            organisationId: this.organisation.id,
            sorting: this.columnSorting,
            text: this.textSearch,
            ...parsedTableSearch
        });
    }

    refreshSubscriptions() {
        combineLatest([
            this.lotamDataService.lotamSource$,
            this.lotamDataService.currentPage$,
            this.lotamDataService.totalItems$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([lotams, currentPage, totalItems]) => {
                this.lotams = lotams.map(item => ({
                    ...item,
                    isAcknowledged: item.currentUserAcknowledgementTime !== null
                }));
                if (this.tableConfig != null) {
                    const currentConfig = this.tableConfig;
                    this.tableConfig = {
                        ...currentConfig,
                        currentPage: currentPage,
                        totalItems: totalItems
                    };
                }
            });
        this.noticesService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.refreshLotam();
            });
    }

    createLotam() {
        this.lotamDialoguesService.showAddLotamDialogue(
            this.organisation,
            'FLIGHT_OPERATIONS'
        );
    }

    onAcknowledge(event: Event, lotam: Lotam) {
        event.preventDefault();
        if (lotam.isAcknowledged) {
            return;
        }
        this.noticesService
            .acknowledgeNotice(lotam.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.logging.success(
                        `Successfully acknowledge LOTAM ${lotam.subject}`
                    );
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while acknowledge LOTAM: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    close() {
        this.modal.hide();
    }

    private buildTableActions(): TableAction<DisplayNotice>[] {
        return [
            {
                action: lotam => this.cloneLotam(lotam),
                icon: 'fal fa-clone',
                tooltip: 'Clone LOTAM',
                // Cloning creates a new notice, so it requires add permission
                disabled: () => !this.canAddNotice
            }
        ];
    }

    cloneLotam(lotam: DisplayNotice) {
        this.lotamDialoguesService.showCloneLotamDialogue(
            this.organisation,
            lotam.id
        );
    }
}
